<template>
 <div class="card">

 <!-- Top row: avatar | name + timing + pill — tap to expand (same layout as SignupCard) -->
 <div class="flex items-start gap-3 cursor-pointer" @click="expanded = !expanded">
 <div
 class="h-14 w-14 rounded-full flex-shrink-0 flex items-center justify-center text-white text-lg font-semibold"
 :style="{ background: avatarGradient(title) }"
 >
 {{ initials(title) || '?' }}
 </div>
 <div class="flex-1 min-w-0">
 <div class="flex justify-between items-start gap-2">
 <span class="font-semibold text-fg-base dark:text-fg-base-dark text-sm leading-tight">{{ title || 'New student' }}</span>
 <div class="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
 <span class="text-xs text-fg-faint dark:text-fg-faint-dark tabular-nums">{{ topBadgeTime }}</span>
 <svg
 :class="['h-3.5 w-3.5 text-fg-faint dark:text-fg-faint-dark transition-transform duration-200', expanded ? 'rotate-180' : '']"
 fill="none" viewBox="0 0 24 24" stroke="currentColor"
 >
 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
 </svg>
 </div>
 </div>
 <div class="mt-1">
 <span :class="['inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold', stage.badgeClass]">{{ stage.label }}</span>
 <span v-if="stageDetail" class="ml-1 text-xs text-fg-muted dark:text-fg-muted-dark">{{ stageDetail }}</span>
 </div>
 <div v-if="times.length" class="mt-1 space-y-0.5">
 <div v-for="(t, i) in times" :key="i"
 :class="['flex items-center gap-1.5 flex-wrap text-xs text-fg-muted dark:text-fg-muted-dark', t.superseded && 'opacity-50']">
 <span v-if="t.kindLabel" class="text-[10px] font-semibold uppercase tracking-wide text-fg-faint dark:text-fg-faint-dark">{{ t.kindLabel }}</span>
 <span :class="t.superseded && 'line-through'"><span class="font-medium text-fg-secondary dark:text-fg-secondary-dark">{{ fmtSlotDate(t.date) }}</span><span v-if="t.time"> · {{ fmtSlotTime(t.time) }}</span><span v-if="t.end">–{{ fmtSlotTime(t.end) }}</span><span v-if="t.suffix"> · {{ t.suffix }}</span></span>
 <button v-if="t.confirmable && confirmable.length > 1" @click.stop="confirmTime(t)" :disabled="!!busy"
 class="action-btn-primary !py-0.5 !px-2">{{ busy === `${t.date} ${t.time}` ? '…' : 'Confirm' }}</button>
 </div>
 </div>
 <p v-else class="mt-1 text-xs text-fg-faint dark:text-fg-faint-dark">No time chosen yet</p>
 </div>
 </div>

 <!-- What the family said: their note with suggested times, or why they turned the offer down -->
 <p v-if="signup.family_note" class="mt-2 text-xs text-fg-secondary dark:text-fg-secondary-dark">
 <span class="text-fg-faint dark:text-fg-faint-dark">From the family:</span> “{{ signup.family_note }}”
 </p>

 <!-- Contact bar + action buttons (same arrangement as SignupCard) -->
 <div class="mt-2.5 flex flex-col gap-2">
 <div class="flex items-center gap-3 min-w-0 overflow-hidden">
 <span v-if="contactName" class="text-xs text-fg-muted dark:text-fg-muted-dark truncate">{{ contactName }}</span>
 <a v-if="signup.phone" :href="`tel:${signup.phone}`" @click.stop
 class="text-xs text-fg-muted dark:text-fg-muted-dark tabular-nums whitespace-nowrap shrink-0">{{ formatPhone(signup.phone) }}</a>
 <a v-if="signup.email" :href="`mailto:${signup.email}`" @click.stop
 class="text-xs text-fg-muted dark:text-fg-muted-dark truncate">{{ signup.email }}</a>
 </div>

 <p v-if="doneMessage" class="text-xs text-green-600 dark:text-green-400 self-end">{{ doneMessage }}</p>
 <div v-else-if="signup.actions?.length && !panel" class="flex items-center gap-1.5 self-end flex-wrap justify-end">
 <button v-if="can('decline')" @click.stop="openPanel('decline')" :disabled="!!busy" class="quick-log-btn" title="Decline">
 <XMarkIcon class="h-4 w-4" />
 </button>
 <button v-if="can('suggest')" @click.stop="openPanel('suggest')" :disabled="!!busy"
 :class="confirmable.length ? 'action-btn-ghost' : 'action-btn-primary'">Suggest Time</button>
 <button v-if="confirmable.length === 1" @click.stop="confirmTime(confirmable[0])" :disabled="!!busy" class="action-btn-primary">
 {{ busy ? '…' : 'Confirm' }}
 </button>
 </div>

 <!-- Decline: confirm first -->
 <div v-if="panel === 'decline'" @click.stop class="flex items-center gap-2 self-end flex-wrap justify-end">
 <span class="text-xs text-fg-secondary dark:text-fg-secondary-dark">Are you sure you want to decline this student?</span>
 <button @click="panel = null" :disabled="busy" class="action-btn-ghost">Cancel</button>
 <button @click="decline" :disabled="busy" class="action-btn-danger">{{ busy ? '…' : 'Decline' }}</button>
 </div>

 <!-- Suggest Time: the same fields as the email's form -->
 <div v-if="panel === 'suggest'" @click.stop class="space-y-2 pt-2 border-t border-stroke dark:border-stroke-dark">
 <p class="text-xs font-medium text-fg-secondary dark:text-fg-secondary-dark">Suggest a different time</p>
 <div v-for="(row, i) in offer" :key="i" class="flex items-center gap-1.5 flex-wrap">
 <input type="date" v-model="row.date" :min="tomorrow" class="offer-input" />
 <input type="time" v-model="row.time" step="900" class="offer-input" />
 <select v-model.number="row.duration" class="offer-input">
 <option v-for="d in [30, 45, 60]" :key="d" :value="d">{{ d }} min</option>
 </select>
 <button v-if="offer.length > 1" @click="offer.splice(i, 1)" class="text-fg-faint dark:text-fg-faint-dark" title="Remove">
 <XMarkIcon class="h-3.5 w-3.5" />
 </button>
 </div>
 <button v-if="offer.length < 5" @click="offer.push({ date: '', time: '', duration: offer[offer.length - 1].duration })"
 class="text-xs text-primary-600 dark:text-primary-400">+ Add another time</button>
 <textarea v-model="offerNote" rows="2" maxlength="1000" placeholder="Note to the family (optional)" class="offer-input w-full"></textarea>
 <div class="flex items-center gap-1.5 justify-end">
 <button @click="panel = null" :disabled="busy" class="action-btn-ghost">Cancel</button>
 <button @click="sendOffer" :disabled="busy || !offerReady" class="action-btn-primary">{{ busy ? '…' : 'Send' }}</button>
 </div>
 </div>

 <p v-if="error" class="text-xs text-red-600 dark:text-red-400 self-end">{{ error }}</p>
 </div>

 <!-- Expanded details -->
 <div v-if="expanded" class="mt-3 pt-3 border-t border-stroke dark:border-stroke-dark space-y-2 text-xs">
 <dl v-if="details.length" class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
 <template v-for="row in details" :key="row.label">
 <dt class="text-fg-faint dark:text-fg-faint-dark">{{ row.label }}</dt>
 <dd class="text-fg-secondary dark:text-fg-secondary-dark break-words">{{ row.value }}</dd>
 </template>
 </dl>
 <p v-if="!signup.full_details" class="text-fg-faint dark:text-fg-faint-dark">
 Full names and contact details appear once the family has registered, added a payment method and signed the agreement.
 </p>

 <!-- This request's back-and-forth with you (teacher_signups._history) -->
 <div class="pt-2 border-t border-stroke dark:border-stroke-dark">
 <p class="font-medium text-fg-faint dark:text-fg-faint-dark uppercase tracking-wide mb-1.5">History</p>
 <p v-if="history === null" class="text-fg-faint dark:text-fg-faint-dark">Loading…</p>
 <p v-else-if="historyError" class="text-fg-faint dark:text-fg-faint-dark">Couldn't load history.</p>
 <ol v-else class="space-y-1.5">
 <li v-for="(e, i) in history" :key="i" class="flex gap-2">
 <span class="text-fg-faint dark:text-fg-faint-dark tabular-nums whitespace-nowrap shrink-0 w-24">{{ fmtWhen(e.at) }}</span>
 <span class="min-w-0">
 <span class="text-fg-secondary dark:text-fg-secondary-dark">{{ e.label }}</span>
 <span v-if="e.detail" class="block text-fg-muted dark:text-fg-muted-dark whitespace-pre-line break-words">{{ e.detail }}</span>
 </span>
 </li>
 </ol>
 </div>
 </div>
 </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useApi } from '../../composables/useApi.js'
import { stageInfo } from '../../composables/useSubmissionStage.js'
import { avatarGradient, initials } from '../../composables/useAvatarGradient.js'
import { useNowTicker, elapsedTime, agoLabel, fmtSlotDate, fmtSlotTime, formatPhone } from '../../composables/useSignupFormat.js'

// Teacher-facing version of SignupCard: same look, only the fields /api/teacher-signups
// sends (api/endpoints/teacher_signups.py), and only the responses the teacher's own
// emails offer — confirm a requested time, suggest other times, decline. The server
// says which apply (`actions`, slot `confirmable`) and re-checks them on every click.
const props = defineProps({ signup: { type: Object, required: true } })
const emit = defineEmits(['updated'])
const { fetchApi } = useApi()
const expanded = ref(false)
const now = useNowTicker()

// ── Responses ────────────────────────────────────────────────────────────────
const panel = ref(null)        // null | 'decline' | 'suggest'
const busy = ref(null)         // falsy, or which button is working
const error = ref('')
const doneMessage = ref('')
const can = action => (props.signup.actions || []).includes(action)
const confirmable = computed(() => props.signup.slots.filter(t => t.confirmable))

const tomorrow = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toLocaleDateString('en-CA') })()
const offer = ref([])
const offerNote = ref('')
const offerReady = computed(() => offer.value.every(r => r.date && r.time))

function openPanel(name) {
 error.value = ''
 if (name === 'suggest') {
  offer.value = [{ date: '', time: '', duration: props.signup.slots[0]?.duration_minutes || 30 }]
  offerNote.value = ''
 }
 panel.value = name
}

async function respond(path, body, busyKey) {
 busy.value = busyKey
 error.value = ''
 try {
  const res = await fetchApi(`/api/teacher-signups/${props.signup.id}/${path}`, {
   method: 'POST',
   headers: { 'Content-Type': 'application/json' },
   body: JSON.stringify(body || {}),
  })
  panel.value = null
  doneMessage.value = res?.message || 'Done'
  setTimeout(() => emit('updated'), 1500)
 } catch (e) {
  error.value = e?.detail || e?.message || 'Something went wrong — please try again.'
 } finally {
  busy.value = null
 }
}

const confirmTime = t => respond('confirm', { date: t.date, time: t.time }, `${t.date} ${t.time}`)

// ── History: fetched the first time the card is expanded ─────────────────────
const history = ref(null)
const historyError = ref(false)
watch(expanded, async open => {
 if (!open || history.value) return
 try {
  history.value = (await fetchApi(`/api/teacher-signups/${props.signup.id}/history`))?.events || []
 } catch {
  history.value = []
  historyError.value = true
 }
})
const fmtWhen = iso => (iso ? new Date(iso).toLocaleString('en-US', {
 month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
}) : '')
const decline = () => respond('decline', null, 'decline')
const sendOffer = () => respond('suggest', { slots: offer.value, note: offerNote.value }, 'suggest')

// Before the family registers the backend sends first names only; Demo Mode's
// name scrambler swaps those for a full fake name, so trim back to the first word.
const shown = name => (!name ? '' : props.signup.full_details ? name : name.split(' ')[0])
const title = computed(() => shown(props.signup.student_name))
const contactName = computed(() => {
 const c = shown(props.signup.contact_name)
 return c && c !== title.value ? c : ''
})
const stage = computed(() => stageInfo(props.signup))

// Where each time came from. Booked series slots need no label (the stage says Scheduled).
const KIND_LABELS = { requested: 'Requested', family_suggested: 'Family suggested', you_offered: 'You offered' }
const times = computed(() => {
 const s = props.signup
 if (s.first_lesson?.date) {
  const f = s.first_lesson
  return [{ date: f.date, time: f.time, suffix: f.duration_minutes ? `${f.duration_minutes} min` : '' }]
 }
 return s.slots.filter(t => t.date).map(t => ({
  ...t, end: t.end_time, kindLabel: KIND_LABELS[t.kind] || '',
  suffix: t.duration_minutes && !t.end_time ? `${t.duration_minutes} min` : '',
 }))
})

const msUntil = t => {
 const [y, mo, d] = t.date.split('-').map(Number)
 const [h, m] = (t.time || '00:00').split(':').map(Number)
 return new Date(y, mo - 1, d, h, m).getTime() - now.value
}

// Same as SignupCard's stageDetail: the backend leaves "Lesson Today" blank and the
// card counts down live.
const stageDetail = computed(() => {
 if (stage.value.detail || stage.value.label !== 'Lesson Today' || !times.value[0]) return stage.value.detail
 const diffMs = msUntil(times.value[0])
 if (diffMs <= 0) return 'now'
 const totalSecs = Math.floor(diffMs / 1000)
 const mins = Math.floor(totalSecs / 60) % 60
 const hrs = Math.floor(totalSecs / 3600)
 if (hrs >= 1) return `in ${hrs}h ${mins}m`
 if (mins >= 1) return `in ${mins}m ${totalSecs % 60}s`
 return `in ${totalSecs}s`
})

// Countdown to the next lesson/requested time; elapsed-since-signup when there is none,
// or once it has passed — same rules as SignupCard's top badge.
const topBadgeTime = computed(() => {
 const t = times.value[0]
 if (!t) return elapsedTime(props.signup.created_at)
 const diffMs = msUntil(t)
 if (diffMs <= 0) return agoLabel(-diffMs)
 // Calendar days, like the stage detail ("First lesson in 2 days"), so the two agree.
 const [y, mo, d] = t.date.split('-').map(Number)
 const today = new Date(now.value)
 const days = Math.round((new Date(y, mo - 1, d) - new Date(today.getFullYear(), today.getMonth(), today.getDate())) / 86400000)
 const totalMins = Math.floor(diffMs / 60000)
 const hrs = Math.floor(totalMins / 60)
 if (days >= 1) return `in ${days}d`
 if (hrs >= 1) return `in ${hrs}hrs`
 return `in ${totalMins}m`
})

const details = computed(() => {
 const s = props.signup
 return [
  s.instrument && { label: 'Instrument', value: s.instrument },
  s.age && { label: 'Age', value: s.age },
  s.experience && { label: 'Experience', value: s.experience },
  s.musical_taste && { label: 'Likes', value: s.musical_taste },
  s.preferred_times && { label: 'Prefers', value: s.preferred_times },
  s.created_at && { label: 'Signed up', value: elapsedTime(s.created_at) },
 ].filter(Boolean)
})
</script>

<style scoped>
.offer-input {
 @apply text-xs px-1.5 py-1 rounded border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-1 focus:ring-primary-400;
}
</style>
